import Seo from "@/components/Seo";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { ArrowUpRight, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message received. Speak soon.");
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-zinc-950 pt-32 pb-20">
      <Seo
        title="Contact"
        description="Get in touch with HighwaysIndustry.com. Discovery calls, content projects and partnership enquiries for the UK highways and infrastructure sector."
        path="/contact"
      />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">Contact</span>
          </div>
          <h1 className="font-heading font-black text-5xl md:text-7xl mt-6 leading-[0.95] tracking-tighter">
            Ready to be seen.
          </h1>
          <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-md">
            Tell us what needs telling, a launch, a project, a person, a
            point of view. We&apos;ll come back fast, honestly, and with a
            real answer.
          </p>

          <div className="mt-10 space-y-6 border-t border-zinc-800 pt-8">
            <div>
              <p className="label-eyebrow text-zinc-500">Email</p>
              <p className="font-heading font-bold text-xl mt-2">
                hello@highwaysmedia.co.uk
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-zinc-500">London HQ</p>
              <p className="text-zinc-300 mt-2 leading-relaxed">
                The Engine Room
                <br />
                London, United Kingdom
              </p>
            </div>
            <div>
              <p className="label-eyebrow text-zinc-500">Looking for the funnel?</p>
              <Link
                to="/positioning-review"
                data-testid="contact-funnel-link"
                className="mt-2 inline-flex items-center gap-2 text-accent-blue font-bold text-sm uppercase tracking-widest"
              >
                Positioning Review → <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="border border-zinc-800 bg-zinc-900/30 p-6 md:p-10 space-y-5"
          >
            {sent ? (
              <div data-testid="contact-success" className="text-center py-12">
                <span className="label-eyebrow text-accent-blue">
                  Message received
                </span>
                <p className="font-heading font-black text-3xl md:text-4xl mt-4">
                  Thanks, you&apos;ll hear from us fast.
                </p>
                <p className="mt-4 text-zinc-400">
                  While you wait: the Positioning Review maps out a tailored
                  12-month plan for putting you in front of the industry.
                </p>
                <Link
                  to="/positioning-review"
                  className="mt-8 inline-flex items-center gap-2 bg-primary-blue text-white px-6 py-3 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all"
                >
                  Start Positioning Review →
                </Link>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    name="name"
                    label="Name"
                    value={form.name}
                    onChange={onChange}
                  />
                  <Field
                    name="company"
                    label="Company"
                    value={form.company}
                    onChange={onChange}
                  />
                </div>
                <Field
                  name="email"
                  type="email"
                  label="Work email"
                  value={form.email}
                  onChange={onChange}
                />
                <div>
                  <label className="label-eyebrow text-zinc-500 block">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    rows={6}
                    value={form.message}
                    onChange={onChange}
                    className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-accent-blue focus:outline-none focus:ring-0 p-4 text-base text-slate-900 placeholder-slate-400"
                    placeholder="What's the story you want the industry to hear?"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send it over <ArrowUpRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}

function Field({ name, label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="label-eyebrow text-zinc-500 block">{label}</label>
      <input
        data-testid={`contact-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-accent-blue focus:outline-none focus:ring-0 p-4 text-base text-slate-900 placeholder-slate-400"
        placeholder={label}
      />
    </div>
  );
}
